import { useState, useEffect } from 'react'
import { k8sResourceService, type K8sResourceListResponse } from '@/services/k8sResourceService'
import SearchableSelector from './SearchableSelector'
import { useTranslation } from 'react-i18next'

interface PVCConfigData {
  enabled: boolean
  pvcName: string
}

interface PVCConfigProps {
  value: PVCConfigData
  onChange: (config: PVCConfigData) => void
  disabled?: boolean
  namespace?: string
  className?: string
}

export default function PVCConfig({
  value,
  onChange,
  disabled = false,
  namespace,
  className = ''
}: PVCConfigProps) {
  const { t } = useTranslation('common')
  const [isExpanded, setIsExpanded] = useState(false)
  const [pvcs, setPvcs] = useState<K8sResourceListResponse | null>(null)
  const [pvcNames, setPvcNames] = useState<string[]>([])
  const [pvcsLoading, setPvcsLoading] = useState(false)

  // Fetch PVCs when expanded and namespace is available
  useEffect(() => {
    const fetchPVCs = async () => {
      if (!isExpanded || !namespace) return

      setPvcsLoading(true)
      try {
        const pvcData = await k8sResourceService.getPVCs(namespace)
        setPvcs(pvcData)
        const names = k8sResourceService.extractPVCNames(pvcData)
        setPvcNames(names)
      } catch (error) {
        console.error('Failed to fetch PVCs:', error)
        setPvcs(null)
        setPvcNames([])
      } finally {
        setPvcsLoading(false)
      }
    }

    fetchPVCs()
  }, [isExpanded, namespace])

  // Get PVC size from the selected PVC
  const getSelectedPVCSize = (): string => {
    if (!value.pvcName || !pvcs) return 'Not selected'
    const size = k8sResourceService.getPVCSize(pvcs, value.pvcName)
    return size || 'Unknown'
  }

  const handleInputChange = (field: keyof PVCConfigData, newValue: any) => {
    onChange({
      ...value,
      [field]: newValue
    })
  }

  const handleToggleEnabled = () => {
    const newEnabled = !value.enabled
    onChange({
      ...value,
      enabled: newEnabled
    })
    if (newEnabled) {
      setIsExpanded(true)
    }
  }

  const ChevronDownIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" />
    </svg>
  )

  const DatabaseIcon = () => (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
    </svg>
  )

  return (
    <div className={`border border-secondary-300 rounded-lg bg-background ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="shrink-0">
            <div className="w-8 h-8 bg-secondary-100 rounded-full flex items-center justify-center">
              <DatabaseIcon />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-text-dark">
              Persistent Volume Claim (PVC)
            </h3>
            <p className="text-xs text-text-dark/60">
              {value.enabled ? `${value.pvcName || 'Not selected'} (${getSelectedPVCSize()})` : 'Enable to add persistent storage'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Enable/Disable Toggle */}
          <button
            type="button"
            onClick={handleToggleEnabled}
            disabled={disabled}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
              value.enabled ? 'bg-primary' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                value.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>

          {/* Expand/Collapse Arrow (only show when enabled) */}
          {value.enabled && (
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              disabled={disabled}
              className="text-text-dark/40 transition-transform duration-200 hover:text-text-dark/60 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}>
                <ChevronDownIcon />
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Expandable Content */}
      {value.enabled && isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-secondary-200 bg-secondary-50/30">
          <div className="pt-4 space-y-4">
            {/* PVC Selector */}
            <div>
              <SearchableSelector
                title={t('flowConfig.selectPvc')}
                value={value.pvcName}
                onChange={(val) => handleInputChange('pvcName', val)}
                options={pvcNames}
                placeholder={t('flowConfig.selectAPvc')}
                loading={pvcsLoading}
                disabled={disabled}
                emptyMessage="No PVCs available in this namespace"
                showCount={false}
              />
              {value.pvcName && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-xs font-medium text-blue-900">{t('flowConfig.selectedPvcInfo')}</p>
                      <div className="mt-1 space-y-1">
                        <p className="text-xs text-blue-700">
                          <span className="font-medium">Name:</span> {value.pvcName}
                        </p>
                        <p className="text-xs text-blue-700">
                          <span className="font-medium">Size:</span> {getSelectedPVCSize()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
